"use client";

import React, {
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Box, Text, Icon, Spinner, IconButton } from "@chakra-ui/react";
import { LuX, LuCheck, LuTriangle, LuInfo } from "react-icons/lu";
import { FaXmark } from "react-icons/fa6";

type ToastColor =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "danger";
type Placement =
  | "bottom-right"
  | "bottom-left"
  | "bottom-center"
  | "top-right"
  | "top-left"
  | "top-center";

export interface ToastProps {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  color?: ToastColor;
  variant?: "solid" | "bordered" | "flat";
  radius?: "none" | "sm" | "md" | "lg" | "full";
  size?: "sm" | "md" | "lg";
  hideIcon?: boolean;
  hideCloseButton?: boolean;
  shouldShowTimeoutProgress?: boolean;
  timeout?: number;
  onClose?: () => void;
  promise?: Promise<unknown>;
  loadingComponent?: ReactNode;
  endContent?: ReactNode;
  closeIcon?: ReactNode;
  classNames?: Partial<Record<string, string>>;
  placement?: Placement;
}

const getDefaultIcon = (color: ToastProps["color"]) => {
  switch (color) {
    case "success":
      return LuCheck;
    case "warning":
      return LuTriangle;
    case "danger":
      return FaXmark;
    case "primary":
    case "secondary":
    case "default":
    default:
      return LuInfo;
  }
};

const getAnimationDirection = (placement: ToastProps["placement"]) => {
  switch (placement) {
    case "bottom-right":
    case "top-right":
      return "translateX(100%)";
    case "bottom-left":
    case "top-left":
      return "translateX(-100%)";
    case "bottom-center":
      return "translateY(100%)";
    case "top-center":
      return "translateY(-100%)";
    default:
      return "translateX(100%)";
  }
};

const getColorStyles = (
  color: ToastProps["color"],
  variant: ToastProps["variant"],
) => {
  const baseStyles = {
    borderColor: "neutral.200",
    bg: "white",
    iconColor: "neutral.500",
    titleColor: "neutral.900",
    descriptionColor: "neutral.600",
    closeButtonColor: "neutral.500",
    closeButtonHoverBg: "neutral.100",
    closeButtonHoverColor: "neutral.700",
    progressColor: "neutral.500",
  };

  const colorMap = {
    primary: { bg: "primary", icon: "primary", text: "primary" },
    success: { bg: "green", icon: "green", text: "green" },
    warning: { bg: "orange", icon: "orange", text: "orange" },
    danger: { bg: "red", icon: "red", text: "red" },
    default: { bg: "gray", icon: "gray", text: "gray" },
  };

  const currentColor = colorMap[(color || "default") as keyof typeof colorMap];

  if (variant === "solid") {
    return {
      ...baseStyles,
      bg: `${currentColor.bg}.500`,
      borderColor: "transparent",
      iconColor: `${currentColor.icon}.200`,
      titleColor: "white",
      descriptionColor: `${currentColor.text}.100`,
      closeButtonColor: `${currentColor.icon}.200`,
      closeButtonHoverBg: `${currentColor.bg}.600`,
      closeButtonHoverColor: "white",
      progressColor: `${currentColor.icon}.200`,
    };
  }

  return {
    ...baseStyles,
    borderColor: `${currentColor.bg}.200`,
    bg: `${currentColor.bg}.50`,
    iconColor: `${currentColor.icon}.500`,
    titleColor: `${currentColor.text}.900`,
    descriptionColor: `${currentColor.text}.700`,
    progressColor: `${currentColor.icon}.500`,
  };
};

const getSizeStyles = (size: ToastProps["size"]) => {
  const sizeMap = {
    sm: {
      padding: 3,
      gap: 2,
      titleFontSize: "xs",
      descriptionFontSize: "xs",
      iconSize: 4,
      closeButtonSize: 5,
    },
    md: {
      padding: 4,
      gap: 3,
      titleFontSize: "sm",
      descriptionFontSize: "sm",
      iconSize: 6,
      closeButtonSize: 6,
    },
    lg: {
      padding: 5,
      gap: 4,
      titleFontSize: "md",
      descriptionFontSize: "md",
      iconSize: 8,
      closeButtonSize: 8,
    },
  };

  return sizeMap[size || "md"];
};

const getRadiusStyles = (radius: ToastProps["radius"]) => {
  const radiusMap = {
    none: { borderRadius: 0 },
    sm: { borderRadius: "sm" },
    md: { borderRadius: "md" },
    lg: { borderRadius: "lg" },
    full: { borderRadius: "full" },
  };

  return radiusMap[radius || "lg"];
};

export const Toast = ({
  title,
  description,
  icon,
  color = "default",
  variant = "flat",
  radius = "lg",
  size = "md",
  hideIcon = false,
  hideCloseButton = false,
  shouldShowTimeoutProgress = true,
  timeout = 6666,
  onClose,
  promise,
  loadingComponent,
  endContent,
  closeIcon,
  classNames = {},
  placement = "bottom-right",
}: ToastProps) => {
  const [progress, setProgress] = useState(100);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const colorStyles = getColorStyles(color, variant);
  const sizeStyles = getSizeStyles(size);
  const radiusStyles = getRadiusStyles(radius);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (promise) {
      setIsLoading(true);
      promise
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [promise]);

  useEffect(() => {
    if (shouldShowTimeoutProgress && timeout > 0) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - 100 / (timeout / 100);
          if (newProgress <= 0) {
            clearInterval(interval);
            handleClose();
            return 0;
          }
          return newProgress;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [shouldShowTimeoutProgress, timeout, handleClose]);

  useEffect(() => {
    if (timeout > 0 && !shouldShowTimeoutProgress) {
      const timer = setTimeout(() => {
        handleClose();
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [timeout, shouldShowTimeoutProgress, handleClose]);

  const defaultIcon = getDefaultIcon(color);
  const IconComponent = icon || defaultIcon;
  const transform = useMemo(() => {
    if (isExiting) {
      return `${getAnimationDirection(placement)} scale(0.95)`;
    }
    if (isVisible) {
      return "translateX(0) translateY(0) scale(1)";
    }
    return `${getAnimationDirection(placement)} scale(0.95)`;
  }, [isExiting, isVisible, placement]);

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={sizeStyles.gap}
      zIndex={10000}
      p={sizeStyles.padding}
      {...radiusStyles}
      shadow={variant === "flat" ? "none" : "lg"}
      borderWidth={
        variant === "solid" ? 0 : variant === "bordered" ? "2px" : "1px"
      }
      borderColor={colorStyles.borderColor}
      bg={colorStyles.bg}
      maxW="400px"
      minW="300px"
      position="relative"
      overflow="hidden"
      className={classNames.base}
      data-has-title={!!title}
      data-has-description={!!description}
      data-placement="bottom-right"
      data-animation={isExiting ? "exiting" : isVisible ? "entering" : "queued"}
      transform={transform}
      opacity={isExiting ? 0 : isVisible ? 1 : 0}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      willChange="transform, opacity"
    >
      {!hideIcon && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          boxSize={sizeStyles.iconSize}
          className={classNames.icon}
        >
          {isLoading ? (
            loadingComponent || <Spinner size="sm" />
          ) : (
            <Icon
              as={IconComponent as React.ElementType}
              color={colorStyles.iconColor}
              boxSize={sizeStyles.iconSize}
            />
          )}
        </Box>
      )}

      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        flex={1}
        minW={0}
        className={classNames.content}
        pr={!hideCloseButton ? sizeStyles.closeButtonSize + 2 : 0}
      >
        {title && (
          <Text
            fontSize={sizeStyles.titleFontSize}
            fontWeight="semibold"
            color={colorStyles.titleColor}
            lineHeight="1.2"
            className={classNames.title}
          >
            {title}
          </Text>
        )}
        {description && (
          <Text
            fontSize={sizeStyles.descriptionFontSize}
            color={colorStyles.descriptionColor}
            lineHeight="1.4"
            className={classNames.description}
          >
            {description}
          </Text>
        )}
      </Box>

      {endContent && <Box className={classNames.endContent}>{endContent}</Box>}

      {!hideCloseButton && (
        <IconButton
          position="absolute"
          top={2}
          right={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          boxSize={sizeStyles.closeButtonSize}
          borderRadius="md"
          bg="transparent"
          color={colorStyles.closeButtonColor}
          cursor="pointer"
          _hover={{
            bg: colorStyles.closeButtonHoverBg,
            color: colorStyles.closeButtonHoverColor,
          }}
          onClick={handleClose}
          aria-label="Close"
          size="sm"
          variant="ghost"
          className={classNames.closeButton}
        >
          <Icon
            as={(closeIcon || LuX) as React.ElementType}
            boxSize={4}
            className={classNames.closeIcon}
          />
        </IconButton>
      )}

      {shouldShowTimeoutProgress && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          h="2px"
          bg="transparent"
          className={classNames.progressTrack}
        >
          <Box
            h="100%"
            bg={colorStyles.progressColor}
            transition="width 0.1s linear"
            w={`${progress}%`}
            className={classNames.progressIndicator}
          />
        </Box>
      )}
    </Box>
  );
};
